import {Component, inject, OnInit} from '@angular/core';
import {FormBuilder, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {IFileOptions} from '../../../../models/file-options.model';
import {MaterialModule} from '../../../../material.module';
import {DatePipe} from '@angular/common';
import {ToastService} from '../../../../services/layout/toast.service';
import {MatDialog} from '@angular/material/dialog';
import {ConfirmDialogComponent} from '../../../confirm-dialog/confirm-dialog.component';
import {FileGeneralSettingsComponent} from '../file-general-settings/file-general-settings.component';
import {FilesService} from "../../../../services/objects/files.service";
import {TabbedPageComponent} from "../../../objects/tabbed-page/tabbed-page.component";
import {ClientObjectSettings} from "../../../objects/client-object-settings/client-object-settings.component";

@Component({
  selector: 'app-file-settings',
  templateUrl: './file-settings.component.html',
  styleUrls: ['./file-settings.component.scss'],
  imports: [
    MaterialModule,
    ReactiveFormsModule,
    FileGeneralSettingsComponent,
    DatePipe,
    ClientObjectSettings
  ],
  standalone: true
})
export class FileSettingsComponent extends TabbedPageComponent implements OnInit{
    private fb = inject(FormBuilder);
    private fileService = inject(FilesService);
    private snackBar = inject(ToastService);
    private dialog = inject(MatDialog);


  isSaving: boolean = false;
  isDeleting: boolean = false;

  file: IFileOptions | null = null;
  id: string = '';

  // Reactive Forms
  settingsForm: FormGroup;

  ngOnInit(): void {
    super.OnInit()

    this.settingsForm = this.fb.group({
      brandDomain: ['', Validators.maxLength(253)],
      market: ['', Validators.maxLength(128)]
    });

    this.route.params.subscribe(params => {
      this.id = params['id'];
      this.loadData().then(() => {
      });
    });
  }

  async loadData() {
    try {
      // Fetch File Data
      const fileResponse = await this.fileService.getByIdAsync(this.id).toPromise();
      this.file = fileResponse as IFileOptions;

      // Populate Settings Form
      this.settingsForm.patchValue({
        name: this.file?.name,
        enabled: this.file?.enabled,
        description: this.file?.description,
        brandDomain: this.file?.brandDomain ?? '',
        market: this.file?.market ?? '',
      });

    } catch (error) {
      console.error('Error loading data', error);
      this.snackBar.showError('Error loading data', 3000);
    }
  }

  save() {
    if (!this.file) return;

    if (this.settingsForm.invalid) {
      this.snackBar.showError('Please fill in all required fields', 3000);
      return;
    }

    this.isSaving = true;

    Object.assign(this.file, this.settingsForm.value);

    // Send Save Request
    this.fileService.saveAsync(this.file).subscribe(() => {
      this.snackBar.showSuccess('Settings saved successfully', 3000);
      this.isSaving = false;
    }, error => {
      console.error('Error saving settings', error);
      this.snackBar.showError('Error saving settings', 3000);
      this.isSaving = false;
    });
  }

  delete() {
    if (!this.file) return;
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '350px',
      data: {
        title: 'Are you sure you want to delete this file?',
        message: 'This will delete the file record. Once deleted, it cannot be rolled back.',
        confirmText: 'Do you want to proceed?',
        confirmButtonText: 'Delete',
        cancelButtonText: 'Cancel'
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.isDeleting = true;
        this.snackBar.showInfo('Deleting file...', 3000);
        this.fileService.deleteAsync(this.file?.id ?? "").subscribe(() => {
          this.snackBar.showSuccess('File deleted successfully.', 3000);
          this.router.navigate(['/ai/files']);
        }, error => {
          console.error('Error deleting file', error);
          this.snackBar.showError('Error deleting file.', 3000);
          this.isDeleting = false;
        });
      } else {
        this.snackBar.showInfo('Delete action canceled.', 3000);
      }
    });
  }

  onfilePublished() {
    this.loadData();
  }

  downloadFile() {
    if (!this.file?.id) return;
    this.fileService.downloadFile(this.file.id).subscribe({
      next: (resp) => {
        const body = resp.body as Blob;
        if (!body) {
          this.snackBar.showError('Empty file received.', 3000);
          return;
        }
        // Try to get filename from Content-Disposition; fallback to stored fileName
        const cd = resp.headers.get('Content-Disposition') || resp.headers.get('content-disposition');
        let filename = this.file?.fileName || 'download.bin';
        if (cd) {
          const match = /filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i.exec(cd);
          const encoded = (match && (match[1] || match[2])) || '';
          if (encoded) {
            try { filename = decodeURIComponent(encoded); } catch { filename = encoded; }
          }
        }
        const url = window.URL.createObjectURL(body);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      },
      error: (err) => {
        console.error('Download failed', err);
        this.snackBar.showError('File download failed.', 3000);
      }
    });
  }
}
